import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiCheckCircle, FiCheck, FiLogOut, FiInfo, FiAlertCircle, FiArrowRight, FiHelpCircle, FiSave } from 'react-icons/fi';
import Header from './Header';
import axios from 'axios';
import '../index.css';
import { endSession } from '../services/session';
import actAppIcon from '../assets/actlogo.png';
import {
  APPLE_ACCOUNT_TASK_INDEX,
  calculateOnboardingProgress,
  getAppleAccountEmail,
  isOnboardingTaskComplete,
  isValidAppleAccountEmail,
  normalizeAppleAccountEmail
} from '../utils/onboardingTasks';

const OnboardingPage = () => {
  const [completedTasks, setCompletedTasks] = useState([]);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [appleAccountEmail, setAppleAccountEmail] = useState('');
  const [savedAppleAccountEmail, setSavedAppleAccountEmail] = useState('');
  const [appleAccountEmailError, setAppleAccountEmailError] = useState('');
  const [appleAccountSaveStatus, setAppleAccountSaveStatus] = useState('idle');
  const [appleAccountSavedAt, setAppleAccountSavedAt] = useState(null);
  const [savingTaskNumber, setSavingTaskNumber] = useState(null);
  const appleAccountAutoSaveTimer = useRef(null);
  const userEmail = localStorage.getItem('currentUser');
  const [startTime] = useState(Date.now());
  // eslint-disable-next-line no-unused-vars
  const [totalTimeSpent, setTotalTimeSpent] = useState(0);

  useEffect(() => {
    if (!userEmail) {
      console.error("User not authenticated");
      window.location.href = '/login'; // Redirect to login if not authenticated
      return;
    }

    const fetchProgress = async () => {
      try {
        console.log(`Fetching progress for user: ${userEmail}`);
        const response = await axios.post('/api/xapi', {
          action: 'getUserProgress',
          email: userEmail,
        });

        const tasks = response.data.completedTasks || [];
        setCompletedTasks(tasks);
        const savedEmail = getAppleAccountEmail({
          appleAccountEmail: response.data.appleAccountEmail,
          tasks
        });
        setAppleAccountEmail(savedEmail);
        setSavedAppleAccountEmail(savedEmail);
        setAppleAccountSaveStatus(savedEmail ? 'saved' : 'idle');
        setAppleAccountSavedAt(
          response.data.appleAccountEmailUpdatedAt
          || tasks[APPLE_ACCOUNT_TASK_INDEX]?.timestamp
          || null
        );
        setProgress(calculateOnboardingProgress(tasks));
      } catch (error) {
        console.error('Error fetching progress:', error);
        setError('Unable to fetch progress. Please try again later.');
      }
    };

    fetchProgress();
  }, [userEmail]);

  useEffect(() => () => {
    if (appleAccountAutoSaveTimer.current) {
      clearTimeout(appleAccountAutoSaveTimer.current);
    }
  }, []);

  // Time tracking effect
  useEffect(() => {
    if (!userEmail) return;

    // Update time every 30 seconds
    const interval = setInterval(() => {
      const elapsedSeconds = Math.floor((Date.now() - startTime) / 1000);
      setTotalTimeSpent(elapsedSeconds);

      // Send time update to server
      axios.post('/api/xapi', {
        action: 'updateUserData',
        email: userEmail,
        updateData: {
          totalTimeSpent: elapsedSeconds
        }
      }).catch(err => {
        console.error('Error updating time:', err);
      });
    }, 30000); // Every 30 seconds

    // Save time on page unload while preserving the HttpOnly session cookie.
    const handleBeforeUnload = () => {
      const elapsedSeconds = Math.floor((Date.now() - startTime) / 1000);
      fetch('/api/xapi', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        keepalive: true,
        body: JSON.stringify({
          action: 'updateUserData',
          email: userEmail,
          updateData: {
            totalTimeSpent: elapsedSeconds
          }
        })
      }).catch(() => {});
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      clearInterval(interval);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Final update when component unmounts
      const elapsedSeconds = Math.floor((Date.now() - startTime) / 1000);
      axios.post('/api/xapi', {
        action: 'updateUserData',
        email: userEmail,
        updateData: {
          totalTimeSpent: elapsedSeconds
        }
      }).catch(err => {
        console.error('Error updating time on unmount:', err);
      });
    };
  }, [userEmail, startTime]);

  const handleCheck = async (
    taskNumber,
    taskResponse = 'Completed',
    { silent = false } = {}
  ) => {
    let normalizedResponse = taskResponse;

    if (taskNumber === APPLE_ACCOUNT_TASK_INDEX) {
      normalizedResponse = normalizeAppleAccountEmail(taskResponse);

      if (!isValidAppleAccountEmail(normalizedResponse)) {
        setAppleAccountEmailError(
          'Please enter the full email shown under your name in iPhone Settings. It should look like name@example.com.'
        );
        setAppleAccountSaveStatus('unsaved');
        return false;
      }

      setAppleAccountSaveStatus('saving');
    }

    setAppleAccountEmailError('');
    setError(null);
    setSavingTaskNumber(taskNumber);
    const completedAt = new Date();

    try {
      console.log(`=== TASK UPDATE START ===`);
      console.log(`Updating task number: ${taskNumber} for user: ${userEmail}`);
      console.log(`Current user email from localStorage:`, userEmail);

      const updatedTasks = completedTasks.map((task, index) =>
        index === taskNumber
          ? {
              ...task,
              completed: true,
              response: normalizedResponse,
              timestamp: completedAt
            }
          : task
      );

      console.log(`Sending API request to update task ${taskNumber}...`);

      const response = await axios.post('/api/xapi', {
        action: 'updateProgress',
        email: userEmail,
        taskNumber: taskNumber,
        response: normalizedResponse,
        timestamp: completedAt,
      });

      console.log('API Response:', response.data);
      console.log('Update result:', response.data.result);

      if (response.data.result && response.data.result.modifiedCount === 0) {
        console.error('WARNING: Task update returned modifiedCount=0. The database was not updated!');
        console.error('This could mean the user email does not match any record in the database.');
        setError('Task update may have failed. Please contact support if this persists.');
      } else {
        console.log('✓ Task successfully updated in database');
      }

      // Only update the state if the API call is successful
      setCompletedTasks(updatedTasks);

      setProgress(calculateOnboardingProgress(updatedTasks));

      if (taskNumber === APPLE_ACCOUNT_TASK_INDEX) {
        setAppleAccountEmail(normalizedResponse);
        setSavedAppleAccountEmail(normalizedResponse);
        setAppleAccountSaveStatus('saved');
        setAppleAccountSavedAt(
          response.data.appleAccountEmailUpdatedAt || completedAt
        );
      }

      if (!silent) {
        setToastMessage(
          taskNumber === APPLE_ACCOUNT_TASK_INDEX
            ? 'Apple Account email saved!'
            : `Task ${taskNumber + 1} completed!`
        );
        setShowSuccessToast(true);
        setTimeout(() => setShowSuccessToast(false), 3000);
      }

      console.log(`Task ${taskNumber} UI updated.`);
      console.log(`=== TASK UPDATE END ===`);
      return true;

    } catch (error) {
      console.error('=== TASK UPDATE ERROR ===');
      console.error('Error type:', error.name);
      console.error('Error message:', error.message);
      console.error('Full error:', error);
      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
      }
      setError(
        error.response?.data?.message
        || `Unable to update task progress: ${error.message}. Please try again later.`
      );
      if (taskNumber === APPLE_ACCOUNT_TASK_INDEX) {
        setAppleAccountSaveStatus('error');
      }
      return false;
    } finally {
      setSavingTaskNumber(null);
    }
  };

  const handleAppleAccountEmailChange = (value) => {
    if (appleAccountAutoSaveTimer.current) {
      clearTimeout(appleAccountAutoSaveTimer.current);
    }

    setAppleAccountEmail(value);
    setAppleAccountEmailError('');
    setError(null);

    const normalizedEmail = normalizeAppleAccountEmail(value);
    if (
      normalizedEmail === savedAppleAccountEmail
      && isValidAppleAccountEmail(normalizedEmail)
    ) {
      setAppleAccountSaveStatus('saved');
      return;
    }

    setAppleAccountSaveStatus(normalizedEmail ? 'unsaved' : 'idle');
    if (!isValidAppleAccountEmail(normalizedEmail)) {
      return;
    }

    appleAccountAutoSaveTimer.current = setTimeout(() => {
      appleAccountAutoSaveTimer.current = null;
      handleCheck(
        APPLE_ACCOUNT_TASK_INDEX,
        normalizedEmail,
        { silent: true }
      );
    }, 1200);
  };

  const handleManualAppleAccountEmailSave = () => {
    if (appleAccountAutoSaveTimer.current) {
      clearTimeout(appleAccountAutoSaveTimer.current);
    }

    handleCheck(APPLE_ACCOUNT_TASK_INDEX, appleAccountEmail);
  };

  const tasks = [
    {
      number: 0,
      title: "Get the Affordable Cleaning Today App",
      description: "We now have our own app. To send you the iPhone app invitation, we need the email connected to your Apple Account. Apple used to call this your Apple ID. It may be an iCloud, Yahoo, Gmail, Outlook, or another email.",
      image: actAppIcon,
      imageAlt: "Affordable Cleaning Today app icon",
      steps: [
        "Open the gray Settings app on your iPhone.",
        "Tap your name or picture at the very top.",
        "Look directly under your name. Type that email below exactly as it appears."
      ],
      requiresAppleAccountEmail: true,
      actionText: "Save email"
    },
    {
      number: 1,
      title: "Service Acknowledgement",
      description: "Keep in mind that all bookings are sight unseen. That by accepting an appointment doesn't mean that it has to be completed. Make sure that the information that is in your service order matches the condition of the house. If it does not match, you need to let the office know ASAP about the condition so we can talk to the client and figure out how to move forward.",
      actionText: "Acknowledge"
    },
    {
      number: 2,
      title: "Square Payroll - Sign up for Direct Deposit",
      description: "During your onboarding, you will receive an email from HR from Square Payroll in order to sign up for payroll. We pay every two weeks, the 15th and the 30th. If you haven't received the payroll email one hour after beginning this, please reach out to us via HR@affordablecleaningtoday.com or on Indeed.",
      actionText: "I received the email and signed up"
    },
    {
      number: 3,
      title: "Sign up to Booking Koala",
      description: "Please wait for the Booking Koala invite email before trying to log in. Attempting to sign in early creates a duplicate account that we have to delete. You'll receive a welcome email titled 'Welcome to Affordable Cleaning Today!' with a link to set your password. Feel free to bookmark the login page below, but leave this task unchecked until that email arrives and you've created your password.",
      link: "https://affordablecleaningtoday.bookingkoala.com/login",
      linkText: "Log into Booking Koala",
      actionText: "Email received and password set"
    },
    {
      number: 4,
      title: "Download the Apps",
      description: "Download the BookingKoala app for your device to manage your jobs on the go.",
      links: [
        { href: "https://apps.apple.com/us/app/bookingkoala-for-providers/id1446530801", text: "iPhone App" },
        { href: "https://play.google.com/store/apps/details?id=com.bookingkoala.bkforproviders&hl=en_US&pli=1", text: "Android App" }
      ],
      actionText: "Apps Downloaded"
    },
    {
      number: 5,
      title: "Save the Concierge Line to your phone",
      description: "Save this phone number: 727-228-6075 for bookings and logistics.",
      actionText: "Saved to Contacts"
    },
    {
      number: 6,
      title: "Essential Equipment Checklist",
      description: "Depending on the tier of job you select, you'll need different cleaning supplies. For standard cleans, you'll need basic equipment like a vacuum and mop. For deeper cleans, you may need additional items like hard water stain removers, mold and mildew cleaners, and pumice stones. Vacuum and mop are mandatory for all job types.",
      list: [
        "All Purpose Cleaner (Zep Industrial Purple Cleaner and Degreaser Concentrate or Simple Green Pro HD Purple Concentrated Cleaner & Degreaser - Heavy Duty recommended)",
        "Microfiber Towels",
        "Glass Cleaner",
        "Gloves (Nitrile)",
        "Tall Kitchen Trash Bags",
        "Toilet Bowl Cleaner/Toilet Bowl Brush",
        "Floor Cleaner",
        "Non-Scratch Sponge",
        "Soft Bristle Brush",
        "Grout Brush",
        "Scouring Stick (Pumice Stone) (For toilets)",
        "Steel Wool Grade 0000",
        "Vacuum <span class='text-danger font-bold'>Mandatory</span>",
        "Mop <span class='text-danger font-bold'>Mandatory</span>",
        "Paper Towels",
        "Hard Water Remover- CLR (CLR Calcium, Lime & Rust Remover) (For Heavy Cleans Only)",
        "Duster",
        "Mold and Mildew Cleaner (For Heavy Cleans Only)",
        "Squeegee and a Scraper",
        "Small Four Step Ladder"
      ],
      actionText: "I understand the equipment requirements",
      customClass: "expanded-width"
    },
    {
      number: 7,
      title: "Set up your Booking Koala Profile",
      description: "Please take the time to set up your profile in Booking Koala. Add a professional photo (customers will see this), set your scheduling availability, and confirm with the concierge team that everything is ready. Keep your availability updated so you can receive job assignments with 48 hours' notice.",
      actionText: "Profile Complete"
    },
    {
      number: 8,
      title: "Prepare for Your First Job",
      description: "Please arrive at least 10 minutes early to the job site. Ask questions to your team leader. You are not required to bring everything during your first few appointments, but please ensure you have sufficient cleaning products to perform the job.",
      actionText: "I understand"
    },
    {
      number: 9,
      title: "Coordinate with your team leader",
      description: "Once all tasks are completed, the concierge team will reach out to you via the concierge number (727-228-6075) listed in task #4.",
      actionText: "Acknowledged"
    }
  ];

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-5xl mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="card mb-8"
          >
            <div className="flex flex-col md:flex-row items-center justify-between mb-6">
              <div>
                <h2 className="text-3xl font-bold mb-2 text-brandDark font-heading">Welcome to ACT Onboarding</h2>
                <p className="text-gray-600">Complete these tasks to get started with Affordable Cleaning Today</p>
              </div>
              <div className="mt-4 md:mt-0 flex items-center gap-4">
                <motion.div 
                  className="flex items-center bg-brandLight text-brandDark px-4 py-2 rounded-full"
                  whileHover={{ scale: 1.05 }}
                >
                  <div className="w-10 h-10 rounded-full flex items-center justify-center bg-white mr-3">
                    <span className="font-bold">{Math.round(progress)}%</span>
                  </div>
                  <span className="font-medium">Completed</span>
                </motion.div>
                
                <motion.a 
                  href="/questions"
                  className="btn btn-primary flex items-center"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <FiHelpCircle className="mr-2" /> Additional Questions
                </motion.a>
              </div>
            </div>
            
            <div className="progress-container">
              <motion.div 
                className="progress-bar"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.8, ease: "easeOut" }}
              />
            </div>
          </motion.div>
          
          {/* Important Information Section */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="bg-orange-50 border-l-4 border-brandAccent p-5 rounded-lg shadow-card mb-8"
          >
            <div className="flex">
              <FiInfo className="text-brandAccent text-2xl flex-shrink-0 mr-4" />
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Important Information</h3>
                <p className="text-gray-700 mb-2">
                  As a subcontractor, the number of jobs you are assigned depends on several factors including performance and customer satisfaction. To increase the volume of work we send you, consistently meet performance goals and provide excellent service.
                </p>
                <p className="text-gray-700">
                  When customers request your services for future cleanings or you develop a strong base of recurring clients, this positively influences the number of jobs you receive.
                </p>
              </div>
            </div>
          </motion.div>
          
          {error && (
            <motion.div 
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="bg-red-50 border-l-4 border-danger p-4 rounded-lg mb-6 flex items-center"
            >
              <FiAlertCircle className="text-danger mr-3 text-xl" />
              <p className="text-danger">{error}</p>
            </motion.div>
          )}
          
          {/* Task list */}
          <div className="space-y-6">
            {tasks.map((task, index) => {
              const isCompleted = isOnboardingTaskComplete(
                completedTasks[task.number],
                task.number
              );

              return (
                <TaskItem
                  key={task.number}
                  task={task}
                  isCompleted={isCompleted}
                  isSaving={savingTaskNumber === task.number}
                  responseValue={task.requiresAppleAccountEmail ? appleAccountEmail : ''}
                  responseError={task.requiresAppleAccountEmail ? appleAccountEmailError : ''}
                  responseSaveStatus={
                    task.requiresAppleAccountEmail ? appleAccountSaveStatus : 'idle'
                  }
                  responseSavedAt={
                    task.requiresAppleAccountEmail ? appleAccountSavedAt : null
                  }
                  isResponseSaved={
                    task.requiresAppleAccountEmail
                    && isValidAppleAccountEmail(appleAccountEmail)
                    && normalizeAppleAccountEmail(appleAccountEmail) === savedAppleAccountEmail
                  }
                  isResponseValid={
                    task.requiresAppleAccountEmail
                    && isValidAppleAccountEmail(appleAccountEmail)
                  }
                  onResponseChange={handleAppleAccountEmailChange}
                  onCheck={() => {
                    if (task.requiresAppleAccountEmail) {
                      handleManualAppleAccountEmailSave();
                    } else if (!isCompleted) {
                      handleCheck(
                        task.number,
                        'Completed'
                      );
                    }
                  }}
                  animationDelay={0.1 * index}
                />
              );
            })}
          </div>
          
          <motion.button 
            onClick={async () => {
              await endSession();
              window.location.href = '/login';
            }}
            className="mt-8 btn btn-danger flex items-center mx-auto"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <FiLogOut className="mr-2" />
            Logout
          </motion.button>
        </div>
      </div>
      
      {/* Toast Notification */}
      <AnimatePresence>
        {showSuccessToast && (
          <motion.div 
            className="toast toast-success flex items-center"
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
          >
            <FiCheckCircle className="mr-2" />
            {toastMessage}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// Task Item Component
const TaskItem = ({
  task,
  isCompleted,
  isSaving,
  responseValue,
  responseError,
  responseSaveStatus,
  responseSavedAt,
  isResponseSaved,
  isResponseValid,
  onResponseChange,
  onCheck,
  animationDelay
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: animationDelay, duration: 0.5 }}
      className={`task-item ${isCompleted ? 'completed' : ''}`}
    >
      <div className="flex flex-col gap-4 md:flex-row md:justify-between md:items-start">
        <div className="flex-grow pr-4">
          <div className="flex items-center mb-3">
            <div className={`flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center mr-3 ${isCompleted ? 'bg-success text-white' : 'bg-gray-200 text-gray-600'}`}>
              {isCompleted ? <FiCheck className="text-lg" /> : task.number + 1}
            </div>
            <h3 className="text-xl font-semibold font-heading">{task.title}</h3>
          </div>
          
          {task.description && (
            <div className="ml-11 text-gray-600 mb-3">{task.description}</div>
          )}

          {task.requiresAppleAccountEmail && (
            <div className="ml-0 sm:ml-11 mb-4 rounded-xl border border-blue-200 bg-blue-50 p-4">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
                <img
                  src={task.image}
                  alt={task.imageAlt}
                  className="h-32 w-32 flex-shrink-0 self-center rounded-2xl border-4 border-white object-cover shadow-md sm:self-start"
                />
                <div className="min-w-0 flex-grow">
                  <p className="font-semibold text-brandDark">
                    How to find the right email on your iPhone
                  </p>
                  <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm text-gray-700">
                    {task.steps.map((step) => (
                      <li key={step}>{step}</li>
                    ))}
                  </ol>
                  <p className="mt-3 rounded-lg bg-white p-3 text-sm text-gray-700">
                    <strong>It does not have to be an iCloud email.</strong> It may be Yahoo,
                    Gmail, Outlook, iCloud, or another email. Type whichever email is shown
                    directly under your name in Settings, even if it is different from the email
                    you used to sign in here.
                    Please do not send us your Apple password or iPhone passcode.
                  </p>
                </div>
              </div>

              <label
                htmlFor="apple-account-email"
                className="mt-4 block text-sm font-semibold text-gray-800"
              >
                Email shown under your name in iPhone Settings
              </label>
              <input
                id="apple-account-email"
                type="email"
                inputMode="email"
                autoComplete="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                value={responseValue}
                onChange={(event) => onResponseChange(event.target.value)}
                placeholder="name@example.com"
                disabled={isSaving}
                aria-invalid={Boolean(responseError)}
                aria-describedby={responseError ? 'apple-account-email-error' : 'apple-account-email-help'}
                className="form-input mt-2 bg-white"
              />
              <p id="apple-account-email-help" className="mt-2 text-sm text-gray-600">
                This saves automatically after you stop typing. You can also tap Save email.
              </p>
              {responseError && (
                <p
                  id="apple-account-email-error"
                  role="alert"
                  className="mt-2 flex items-start text-sm font-medium text-red-700"
                >
                  <FiAlertCircle className="mr-2 mt-0.5 flex-shrink-0" />
                  {responseError}
                </p>
              )}
              <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center">
                <button
                  type="button"
                  onClick={onCheck}
                  disabled={isSaving}
                  className={`btn btn-primary inline-flex items-center justify-center ${
                    isSaving ? 'cursor-wait opacity-70' : ''
                  }`}
                >
                  <FiSave className="mr-2" />
                  {isSaving ? 'Saving...' : task.actionText}
                </button>
                <div
                  role="status"
                  aria-live="polite"
                  className={`flex items-center text-sm font-medium ${
                    responseSaveStatus === 'error'
                      ? 'text-red-700'
                      : isResponseSaved
                      ? 'text-green-700'
                      : 'text-gray-600'
                  }`}
                >
                  {responseSaveStatus === 'saving' ? (
                    'Saving your email...'
                  ) : isResponseSaved ? (
                    <>
                      <FiCheckCircle className="mr-1.5 flex-shrink-0" />
                      Saved
                      {responseSavedAt && (
                        <span className="ml-1 font-normal text-gray-500">
                          {new Date(responseSavedAt).toLocaleTimeString([], {
                            hour: 'numeric',
                            minute: '2-digit'
                          })}
                        </span>
                      )}
                    </>
                  ) : responseSaveStatus === 'error' ? (
                    'Not saved — tap Save email to try again.'
                  ) : isResponseValid ? (
                    'Ready — this will auto-save when you stop typing.'
                  ) : responseValue.trim() ? (
                    'Not saved yet — enter the complete email.'
                  ) : (
                    'Not saved yet.'
                  )}
                </div>
              </div>
            </div>
          )}
          
          <AnimatePresence>
            {(isExpanded || !task.list) && (
              <>
                {task.list && (
                  <motion.div 
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="ml-11 bg-gray-50 p-4 rounded-lg mb-3"
                  >
                    <h4 className="font-medium mb-2">Required Equipment:</h4>
                    <ul className="list-disc pl-5 text-gray-600">
                      {task.list.map((item, index) => (
                        <li key={index} dangerouslySetInnerHTML={{ __html: item }} className="mb-1" />
                      ))}
                    </ul>
                  </motion.div>
                )}
                
                {task.link && (
                  <div className="ml-11 mb-3">
                    <a
                      href={task.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-primary inline-flex items-center"
                    >
                      {task.linkText} <FiArrowRight className="ml-2" />
                    </a>
                  </div>
                )}
                
                {task.links && (
                  <div className="ml-11 mb-3 flex flex-wrap gap-3">
                    {task.links.map((link, index) => (
                      <a
                        key={index}
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-primary inline-flex items-center"
                      >
                        {link.text} <FiArrowRight className="ml-2" />
                      </a>
                    ))}
                  </div>
                )}
              </>
            )}
          </AnimatePresence>
          
          {task.list && (
            <button 
              onClick={() => setIsExpanded(!isExpanded)} 
              className="ml-11 text-brandBlue hover:underline flex items-center text-sm"
            >
              {isExpanded ? 'Hide equipment list' : 'View equipment list'}
            </button>
          )}
        </div>
        
        {!task.requiresAppleAccountEmail && (
        <div className="ml-11 flex flex-shrink-0 items-center md:ml-0">
          <button
            type="button"
            onClick={onCheck}
            disabled={isCompleted || isSaving}
            className={`flex items-center px-4 py-2 rounded-lg transition-all duration-200 ${
              isCompleted 
                ? 'bg-green-100 text-success cursor-default'
                : isSaving
                ? 'bg-gray-200 text-gray-500 cursor-wait'
                : 'bg-gray-100 hover:bg-brandLight text-gray-700 hover:text-brandDark'
            }`}
          >
            {isCompleted ? (
              <>
                <FiCheck className="mr-2" />
                Completed
              </>
            ) : isSaving ? (
              'Saving...'
            ) : (
              task.actionText
            )}
          </button>
        </div>
        )}
      </div>
    </motion.div>
  );
};

export default OnboardingPage;
